import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes before timeout
const STAY_LOGGED_IN_KEY = 'bawarchi-stay-logged-in';

export const getStayLoggedIn = (): boolean => {
  return localStorage.getItem(STAY_LOGGED_IN_KEY) === 'true';
};

export const setStayLoggedIn = (value: boolean): void => {
  if (value) {
    localStorage.setItem(STAY_LOGGED_IN_KEY, 'true');
  } else {
    localStorage.removeItem(STAY_LOGGED_IN_KEY);
  }
};

export const useSessionTimeout = (enabled: boolean = true) => {
  const { toast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const hasWarnedRef = useRef(false);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error("Session timeout sign out error:", error);
    }
    // Force clear localStorage (except stay logged in preference)
    const projectId = 'rtbbonqykwfiuesigcjl';
    localStorage.removeItem(`sb-${projectId}-auth-token`);
    
    toast({
      title: "Session Expired",
      description: "You have been signed out due to inactivity.",
      variant: "destructive",
    });
    
    window.location.href = "/customer-auth";
  }, [toast]);

  const showWarning = useCallback(() => {
    if (!hasWarnedRef.current) {
      hasWarnedRef.current = true;
      toast({
        title: "Session Expiring Soon",
        description: "Your session will expire in 5 minutes due to inactivity.",
      });
    }
  }, [toast]);

  const resetTimeout = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }
    
    hasWarnedRef.current = false;

    // Check if "Stay logged in" is enabled - skip timeout if so
    if (getStayLoggedIn()) {
      return;
    }

    // Set warning timer (5 minutes before timeout)
    warningRef.current = setTimeout(() => {
      showWarning();
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleSignOut();
    }, SESSION_TIMEOUT_MS);
  }, [handleSignOut, showWarning]);

  useEffect(() => {
    if (!enabled) return;

    // If "Stay logged in" is enabled, don't set up timeout
    if (getStayLoggedIn()) {
      return;
    }

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle the reset to avoid too many calls
    let lastActivity = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastActivity > 1000) { // Only reset if more than 1 second since last activity
        lastActivity = now;
        resetTimeout();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timeout
    resetTimeout();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
    };
  }, [enabled, resetTimeout]);

  return { resetTimeout };
};
